import React, { useMemo, ReactElement } from 'react';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import Table from '../../../../components/table/table.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import FileLink from '../../../../components/file-link/file-link.component';
import FilteredText from '../../../../components/filtered-text/filtered-text.component';
import RichTextBlock from '../../../../components/rich-text-block/rich-text-block.component';
import RequiredMark from '../../../../components/required-mark/required-mark.component';
import FormFieldSelect from '../../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import FormFieldTextRich from '../../../../components/form-field-text-rich/form-field-text-rich.component';
import FormFieldUpload from '../../../../components/form-field-upload/form-field-upload.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import SinglePageTab from '../../../app/common/single-page-tab/single-page-tab.component';
import { ACCOUNT_USER_TYPE_SYSTEM_ADMIN } from '../../../../constants/entity/account-user-types';

const SettingsAccountSummary: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    accountData,
    accountAttachments,
    accountAttachmentFiles,
    accountSubscriptionPlanList,
    roleList,
    currentUser,
    formError,
    onCancelForm,
    onSubmitForm,
    onAccountSummaryChange,
    onAccountAttachmentFileError,
    onAccountAttachmentFileDelete,
    onAccountAttachmentFilesChange,
  } = props;

  const formPrefix: string = 'account';
  const isCurrentUserSystemAdmin: boolean = !!currentUser && !!currentUser.accountUserType
    && currentUser.accountUserType === ACCOUNT_USER_TYPE_SYSTEM_ADMIN;
  const fileDropDeps: DepsItem[] = useMemo((): DepsItem[] => [accountAttachmentFiles], [accountAttachmentFiles]);
  const commonAttachmentFiles: (AttachmentInput | File)[] = useMemo((): (AttachmentInput | File)[] => [
    ...(accountAttachments || []),
    ...(accountAttachmentFiles || []),
  ], [accountAttachments, accountAttachmentFiles]);

  const menuItemRoleList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: roleList,
  }), [roleList]);

  const menuItemAccountSubscriptionPlanList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: accountSubscriptionPlanList,
  }), [accountSubscriptionPlanList]);

  return (
    <SinglePageTab
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      <Table>
        <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
          <TableRow>
            <TableCell>
              Name
              {
                isEditMode && <RequiredMark />
              }
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldText isBordered name={`${formPrefix}.name`} />
                ) : accountData.name
              }
            </TableCell>
          </TableRow>
          {
            (accountData.shortName || isEditMode) && (
              <TableRow>
                <TableCell>
                  Short Name
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.shortName`} />
                    ) : accountData.shortName
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (accountData.role || isEditMode) && (
              <TableRow>
                <TableCell>
                  Role
                  {
                    isEditMode && <RequiredMark />
                  }
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        isDisabled={roleList && !roleList.length}
                        name={`${formPrefix}.role`}
                        optionsTotal={roleList && roleList.length}
                      >
                        {menuItemRoleList}
                      </FormFieldSelect>
                    ) : accountData.role.title
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            isCurrentUserSystemAdmin && (accountData.accountSubscriptionPlan || isEditMode) && (
              <TableRow>
                <TableCell>
                  Subscription Plan
                  {
                    isEditMode && <RequiredMark />
                  }
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        isDisabled={accountSubscriptionPlanList && !accountSubscriptionPlanList.length}
                        name={`${formPrefix}.accountSubscriptionPlan`}
                        optionsTotal={accountSubscriptionPlanList && accountSubscriptionPlanList.length}
                      >
                        {menuItemAccountSubscriptionPlanList}
                      </FormFieldSelect>
                    ) : accountData.accountSubscriptionPlan.title
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (accountData.summary || isEditMode) && (
              <TableRow>
                <TableCell>
                  Summary
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldTextRich name={`${formPrefix}.summary`} onFieldChange={onAccountSummaryChange} />
                    ) : (
                      <RichTextBlock>
                        <FilteredText text={accountData.summary} />
                      </RichTextBlock>
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            ((accountData.attachments && !!accountData.attachments.length) || isEditMode) && (
              <TableRow>
                <TableCell>
                  Attachments
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldUpload
                        isDroppable
                        isMultiple
                        withDirectorySupport
                        withMultipleSelection
                        uploadFiles={commonAttachmentFiles}
                        dropDeps={fileDropDeps}
                        name={`${formPrefix}.attachments`}
                        buttonLabel="Attach Files"
                        onFilesChange={onAccountAttachmentFilesChange}
                        onFileDelete={onAccountAttachmentFileDelete}
                        onError={onAccountAttachmentFileError}
                      />
                    ) : (
                      accountData.attachments && !!accountData.attachments.length &&
                      accountData.attachments.map((attachmentItem: Attachment): ReactElement => (
                        <FlexContainer isInline key={attachmentItem.id}>
                          <FileLink fileLink={attachmentItem.file} />
                        </FlexContainer>
                      ))
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
        </TableBody>
      </Table>
    </SinglePageTab>
  );
});

export default SettingsAccountSummary;
